import { useState } from 'react';
import { Plus, X, Check, AlertCircle, Loader2 } from 'lucide-react';
import { api, ApiError } from '../api/client';

interface QuestionState {
  text: string;
  status: 'idle' | 'checking' | 'valid' | 'invalid';
  reason?: string;
}

export interface SellChecklistEditorProps {
  questions: string[];
  onChange: (questions: string[]) => void;
  onValidityChange?: (allValid: boolean) => void;
  disabled?: boolean;
  placeholder?: string;
}

export default function SellChecklistEditor({
  questions,
  onChange,
  onValidityChange,
  disabled,
  placeholder = 'e.g. "Has the stock dropped more than 20% from entry?"',
}: SellChecklistEditorProps) {
  const [states, setStates] = useState<QuestionState[]>(() =>
    questions.map((text) => ({ text, status: 'idle' })),
  );

  function syncOut(next: QuestionState[]) {
    setStates(next);
    onChange(next.map((s) => s.text));
    if (onValidityChange) {
      const allValid =
        next.length > 0 && next.every((s) => s.status === 'valid' && s.text.trim().length > 0);
      onValidityChange(allValid);
    }
  }

  function addQuestion() {
    syncOut([...states, { text: '', status: 'idle' }]);
  }

  function removeQuestion(index: number) {
    syncOut(states.filter((_, i) => i !== index));
  }

  function updateText(index: number, text: string) {
    const next = states.map((s, i) => (i === index ? { ...s, text, status: 'idle' as const } : s));
    syncOut(next);
  }

  async function validateOne(index: number) {
    const q = states[index]?.text.trim() ?? '';
    if (!q) return;
    setStates((prev) =>
      prev.map((s, i) => (i === index ? { ...s, status: 'checking' } : s)),
    );
    try {
      const result = await api<{ valid: boolean; reason: string }>('/validate/yes-no-question', {
        method: 'POST',
        json: { question: q },
      });
      setStates((prev) => {
        const next = prev.map((s, i) =>
          i === index
            ? {
                ...s,
                status: result.valid ? ('valid' as const) : ('invalid' as const),
                reason: result.reason,
              }
            : s,
        );
        if (onValidityChange) {
          const allValid =
            next.length > 0 && next.every((s) => s.status === 'valid' && s.text.trim().length > 0);
          onValidityChange(allValid);
        }
        return next;
      });
    } catch (err) {
      setStates((prev) =>
        prev.map((s, i) =>
          i === index
            ? {
                ...s,
                status: 'invalid',
                reason: err instanceof ApiError ? err.message : 'Validation failed',
              }
            : s,
        ),
      );
    }
  }

  return (
    <div className="space-y-2">
      {states.map((s, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="flex-1"
                placeholder={placeholder}
                value={s.text}
                disabled={disabled}
                onChange={(e) => updateText(i, e.target.value)}
                onBlur={() => validateOne(i)}
              />
              <span className="w-5 flex items-center justify-center">
                {s.status === 'checking' && <Loader2 size={16} className="animate-spin text-zinc-400" />}
                {s.status === 'valid' && <Check size={16} className="text-emerald-400" />}
                {s.status === 'invalid' && <AlertCircle size={16} className="text-red-400" />}
              </span>
              <button
                type="button"
                onClick={() => removeQuestion(i)}
                disabled={disabled}
                className="text-zinc-500 hover:text-red-400"
                aria-label="Remove"
              >
                <X size={16} />
              </button>
            </div>
            {s.status === 'invalid' && s.reason && (
              <p className="field-error">{s.reason}</p>
            )}
          </div>
        </div>
      ))}
      <button
        type="button"
        className="btn-secondary"
        onClick={addQuestion}
        disabled={disabled}
      >
        <Plus size={16} /> Add question
      </button>
      <p className="text-xs text-zinc-500">
        Each question must be answerable yes/no. Validated by OpenRouter on blur.
      </p>
    </div>
  );
}
